/**
 * MUI theme provider for the vendored gen-process components.
 *
 * The source components are built on MUI; twasta uses a CSS-variable design
 * system. We wrap the vendored subtree in a MUI ThemeProvider whose palette
 * mode + accent follow twasta's dark/light theme so the two blend as closely as
 * possible. CssBaseline is intentionally NOT used (it would reset twasta's
 * global styles); only the palette/typography are themed.
 */

import { useMemo } from 'react'
import type { ReactNode } from 'react'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import { useThemeStore } from '@/store/themeStore'

// twasta accent (var(--color-accent)); secondary mirrors the "Execute" accent.
// MUI's createTheme needs literals, so mirror the per-theme accent values.
const ACCENT_LIGHT = '#0061D0'
const ACCENT_DARK = '#4c8dff'
const SECONDARY = '#7c5cff'

export default function GenprocMuiProvider({ children }: { children: ReactNode }) {
  const theme = useThemeStore((s) => s.theme)
  const muiTheme = useMemo(
    () =>
      createTheme({
        palette:
          theme === 'light'
            ? {
                mode: 'light',
                primary: { main: ACCENT_LIGHT },
                secondary: { main: SECONDARY },
              }
            : {
                mode: 'dark',
                primary: { main: ACCENT_DARK },
                secondary: { main: SECONDARY },
                background: { default: '#12151b', paper: '#171a21' },
              },
        typography: { fontSize: 14 },
        shape: { borderRadius: 6 },
      }),
    [theme],
  )
  return <ThemeProvider theme={muiTheme}>{children}</ThemeProvider>
}
